import { defineStore } from 'pinia'

import * as repairSuggestionApi from '../services/api/repair-suggestion'
import { IApproval } from './staff'

export interface IRepairSuggestionData {
  id: number
  status: string
  type: string
  note: string
  departments: string[]
  reason: string
  approvals: Array<IApproval>
  items: Array<string>
  assignee_id: number
  leader_id: number
  manager_id: number
  service_adviser_id: number
  service_manager_id: number
  car: any
  customer: any
  created_at: string
}

export const useRepairSuggestionStore = defineStore('repairSuggestion', {
  state: () => ({
    repairSuggestion: {
      id: 0,
      status: '',
      type: '',
      note: '',
      departments: [],
      reason: '',
      approvals: [],
      items: [],
      assignee_id: 0,
      leader_id: 0,
      manager_id: 0,
      service_adviser_id: 0,
      service_manager_id: 0,
      customer: null,
      car: null,
      created_at: '',
    } as IRepairSuggestionData,
    isLoading: false,
  }),

  actions: {
    setRepairSuggestion(data: IRepairSuggestionData) {
      this.repairSuggestion = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getRepairSuggestion(repairSuggestion_id: number) {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.get(repairSuggestion_id)
        this.setRepairSuggestion(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateRepairSuggestion(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.update(this.repairSuggestion.id, payload)
        this.setRepairSuggestion(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyRepairSuggestion() {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.apply(this.repairSuggestion.id)
        this.setRepairSuggestion(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectRepairSuggestion(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.reject(this.repairSuggestion.id, payload)
        this.setRepairSuggestion(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeRepairSuggestion(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.remake(this.repairSuggestion.id, payload)
        this.setRepairSuggestion(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveRepairSuggestion(params: any) {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.approve(this.repairSuggestion.id, params)
        this.setRepairSuggestion(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useRepairSuggestionListStore = defineStore('repairSuggestionList', {
  state: () => ({
    repairSuggestions: [] as IRepairSuggestionData[],
    count: 0,
    total: 0,
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addRepairSuggestion(data: IRepairSuggestionData) {
      this.repairSuggestions.push(data)
    },

    setRepairSuggestions(data: IRepairSuggestionData[]) {
      this.repairSuggestions = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countRepairSuggestions() {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createRepairSuggestion(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairSuggestionApi.create(payload)
        this.addRepairSuggestion(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listRepairSuggestions(params: any) {
      try {
        this.setLoading(true)
        const result = await repairSuggestionApi.list(params)
        const { data, total } = result as any
        this.setRepairSuggestions(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllRepairSuggestions(params: any) {
      try {
        this.setLoading(true)
        const result = await repairSuggestionApi.listAllRepairSuggestion(params)
        const { data, total } = result as any
        this.setRepairSuggestions(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
